import { useEffect, useRef, useState, type FormEvent } from 'react'
import { CheckCircle, Copy, PaperPlaneTilt, WarningCircle } from '@phosphor-icons/react'
import { contactForm as f } from '@/content/contact'
import { site } from '@/content/site'
import { contactText, isCompleteMessage, missingFields, type ContactMessage } from '@/lib/mail'
import { ENDPOINT, submitLead, SubmitError, toLead, type SubmitResult } from '@/lib/contact'

type Status = 'idle' | 'error' | 'sending' | 'sent'

/** What the mascot beside the card title shows: at rest, after a send, after a failed send. */
export type FormMood = 'rest' | 'done' | 'failed'

const FIELDS = ['firstName', 'lastName', 'email', 'message'] as const

/**
 * The bottleneck form. With VITE_CONTACT_ENDPOINT set, a complete message is POSTed as JSON to it
 * (lib/contact.ts, ported from v1); a failed post shows the reason and the address to write to.
 * Without the endpoint, it opens the visitor's mail app with the message laid out and addressed, and
 * the sent panel also gives the address and the message to copy, because a visitor with no mail app
 * set up sees nothing open. An incomplete one marks the fields, focuses the first, and shakes the button.
 */
export function ContactForm({ nested = false, onMood }: { nested?: boolean; onMood?: (mood: FormMood) => void }) {
  const Done = nested ? 'h4' : 'h3'
  const [status, setStatus] = useState<Status>('idle')
  const [invalid, setInvalid] = useState<string[]>([])
  const [sent, setSent] = useState<ContactMessage | null>(null)
  const [via, setVia] = useState<SubmitResult['via']>('mailto')
  const [failure, setFailure] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  const sendRef = useRef<HTMLButtonElement>(null)
  const doneRef = useRef<HTMLHeadingElement>(null)
  const refocus = useRef<'done' | 'form' | null>(null)

  const mood: FormMood = status === 'sent' ? 'done' : status === 'error' && failure ? 'failed' : 'rest'
  useEffect(() => onMood?.(mood), [mood, onMood])

  // Focus follows the panel that just appeared (sent state, or the form again after "Write another").
  useEffect(() => {
    if (refocus.current === 'done') doneRef.current?.focus()
    if (refocus.current === 'form') formRef.current?.querySelector<HTMLInputElement>('input[name="firstName"]')?.focus()
    refocus.current = null
  }, [status])

  const shake = () => {
    // Restart the shake on every failed try without remounting the button (that would drop focus).
    const send = sendRef.current
    if (send) {
      send.classList.remove('is-shaking')
      void send.offsetWidth
      send.classList.add('is-shaking')
    }
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (status === 'sending') return
    const data = new FormData(event.currentTarget)
    const message = Object.fromEntries(FIELDS.map((k) => [k, String(data.get(k) ?? '')])) as ContactMessage
    if (!isCompleteMessage(message)) {
      const missing = missingFields(message)
      setInvalid(missing)
      setFailure(null)
      setStatus('error')
      formRef.current?.querySelector<HTMLElement>(`[name="${missing[0]}"]`)?.focus()
      shake()
      return
    }
    setInvalid([])
    setFailure(null)
    setStatus('sending')
    try {
      const result = await submitLead(toLead(message, String(data.get('website') ?? '')))
      setVia(result.via)
      setSent(message)
      setCopied(false)
      refocus.current = 'done'
      setStatus('sent')
    } catch (err) {
      setFailure(err instanceof SubmitError ? err.message : f.failed)
      setStatus('error')
      shake()
    }
  }

  const copyMessage = async () => {
    if (!sent) return
    try {
      await navigator.clipboard.writeText(contactText(sent))
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  if (status === 'sent') {
    return (
      <div className="ct-done" role="status">
        <CheckCircle size={30} weight="fill" aria-hidden />
        <Done ref={doneRef} tabIndex={-1}>
          {via === 'webhook' ? f.postedTitle : f.sentTitle}
        </Done>
        <p>{via === 'webhook' ? f.postedBody : f.sentBody}</p>
        {via === 'mailto' ? (
          <p className="ct-done__fallback">
            {f.noMailApp}{' '}
            <a href={`mailto:${site.email}`}>{site.email}</a>
          </p>
        ) : null}
        <div className="ct-done__actions">
          {via === 'mailto' ? (
            <button type="button" className="pg-btn" onClick={copyMessage}>
              <Copy size={16} aria-hidden />
              {copied ? f.copied : f.copy}
            </button>
          ) : null}
          <button
            type="button"
            className="pg-btn"
            onClick={() => {
              refocus.current = 'form'
              setStatus('idle')
            }}
          >
            {f.again}
          </button>
        </div>
      </div>
    )
  }

  const field = (name: (typeof FIELDS)[number]) => ({ name, 'aria-invalid': invalid.includes(name) || undefined, 'aria-describedby': invalid.includes(name) ? 'ct-err' : undefined })

  return (
    <form ref={formRef} className="ct-form" noValidate onSubmit={onSubmit} aria-busy={status === 'sending' || undefined}>
      {/* Honeypot (v1): hidden from people and assistive tech; a script that fills every field trips it. */}
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="ct-trap" />
      <div className="ct-row2">
        <label className="ct-field">
          <span>{f.first}</span>
          <input type="text" {...field('firstName')} autoComplete="given-name" required maxLength={80} placeholder={f.phFirst} />
        </label>
        <label className="ct-field">
          <span>{f.last}</span>
          <input type="text" {...field('lastName')} autoComplete="family-name" required maxLength={80} placeholder={f.phLast} />
        </label>
      </div>
      <label className="ct-field">
        <span>{f.email}</span>
        <input type="email" {...field('email')} autoComplete="email" required maxLength={254} placeholder={f.phEmail} />
      </label>
      <label className="ct-field">
        <span>{f.message}</span>
        <textarea {...field('message')} required maxLength={ENDPOINT ? 5000 : 1500} placeholder={f.phMessage} />
      </label>
      <div className="ct-actions">
        <button
          ref={sendRef}
          type="submit"
          className="ct-send"
          disabled={status === 'sending'}
          onAnimationEnd={(e) => e.currentTarget.classList.remove('is-shaking')}
        >
          <PaperPlaneTilt size={16} weight="fill" aria-hidden />
          {status === 'sending' ? f.sending : f.send}
        </button>
        {status === 'error' ? (
          <span id="ct-err" className="ct-err" role="alert">
            <WarningCircle size={16} weight="fill" aria-hidden />
            {failure ? (
              <>
                {failure} <a href={`mailto:${site.email}`}>{site.email}</a>
              </>
            ) : (
              f.error
            )}
          </span>
        ) : (
          <span className="ct-hint">{ENDPOINT ? f.hint : f.hintMail}</span>
        )}
      </div>
    </form>
  )
}
